import React from 'react';

import useFeatureFlag from '../../../hooks/useFeatureFlags';
import EmbedYouTubeLinkModal from './EmbedYouTubeLinkModal';
import {
  FIVE_BY_FOUR_CROPPED_IMAGE_ONLY,
  NINE_BY_SIXTEEN_CROPPED_IMAGE_ONLY,
  NINE_BY_SIXTEEN_IMAGE_ONLY,
  ONE_BY_ONE_CROPPED_IMAGE_ONLY,
  TWO_BY_ONE_CROPPED_IMAGE_ONLY,
  TWO_BY_ONE_CROPPED_IMAGE_WITH_ORIGINAL,
  VIDEO_CLOUDINARY,
  VIDEO_YOUTUBE,
} from './mediaTypes';
import UploadImageModal from './UploadImageModal';
import {UploadVideoModal} from './UploadVideoModal';
import type useUploadModal from './useUploadModal';

type UploadModalProps = {
  hintTextKey: string;
  isRemovingEnabled?: boolean;
  enableCroppingTrimming?: boolean;
} & Pick<
  ReturnType<typeof useUploadModal>,
  | 'mediaType'
  | 'imageUrl'
  | 'croppedImageUrl'
  | 'videoUrl'
  | 'croppedVideoUrl'
  | 'embedUrl'
  | 'onSetImage'
  | 'onSetCroppedImage'
  | 'onSetVideo'
  | 'onSetCroppedVideo'
  | 'onSetEmbed'
  | 'onSaveChanges'
  | 'onRemove'
  | 'onClose'
>;

const UploadModal = ({
  mediaType,
  imageUrl,
  croppedImageUrl,
  videoUrl,
  croppedVideoUrl,
  embedUrl,
  hintTextKey,
  onSetImage,
  onSetCroppedImage,
  onSetVideo,
  onSetCroppedVideo,
  onSetEmbed,
  onSaveChanges,
  onRemove,
  onClose,
  isRemovingEnabled = true,
  enableCroppingTrimming = true,
}: UploadModalProps) => {
  const croppingTrimmingFlagEnabled = useFeatureFlag(
    'lessonsVideoSlidesTrimmingCropping'
  );

  const isCroppingTrimmingEnabled =
    enableCroppingTrimming && croppingTrimmingFlagEnabled;

  switch (mediaType) {
    case FIVE_BY_FOUR_CROPPED_IMAGE_ONLY:
    case NINE_BY_SIXTEEN_CROPPED_IMAGE_ONLY:
    case NINE_BY_SIXTEEN_IMAGE_ONLY:
    case TWO_BY_ONE_CROPPED_IMAGE_ONLY:
    case ONE_BY_ONE_CROPPED_IMAGE_ONLY:
    case TWO_BY_ONE_CROPPED_IMAGE_WITH_ORIGINAL: {
      const aspectRatios = {
        [FIVE_BY_FOUR_CROPPED_IMAGE_ONLY]: 5 / 4,
        [NINE_BY_SIXTEEN_CROPPED_IMAGE_ONLY]: 9 / 16,
        [NINE_BY_SIXTEEN_IMAGE_ONLY]: 9 / 16,
        [TWO_BY_ONE_CROPPED_IMAGE_ONLY]: 2,
        [TWO_BY_ONE_CROPPED_IMAGE_WITH_ORIGINAL]: 2,
        [ONE_BY_ONE_CROPPED_IMAGE_ONLY]: 1,
      };

      const aspectRatio = aspectRatios[mediaType];

      return (
        <UploadImageModal
          imageUrl={imageUrl}
          croppedImageUrl={croppedImageUrl}
          cropAspectRatio={aspectRatio}
          showOriginalImage={
            mediaType === TWO_BY_ONE_CROPPED_IMAGE_WITH_ORIGINAL
          }
          hintTextKey={hintTextKey}
          onSetImage={onSetImage}
          onSetCroppedImage={onSetCroppedImage}
          onSaveChanges={onSaveChanges}
          onRemove={onRemove}
          onClose={onClose}
          isRemovingEnabled={isRemovingEnabled}
          mediaType={mediaType}
        />
      );
    }
    case VIDEO_CLOUDINARY:
      return (
        <UploadVideoModal
          videoUrl={videoUrl}
          croppedVideoUrl={croppedVideoUrl}
          hintTextKey={hintTextKey}
          onSetVideo={onSetVideo}
          onSetCroppedVideo={onSetCroppedVideo}
          onSaveChanges={onSaveChanges}
          onRemove={onRemove}
          onClose={onClose}
          isRemovingEnabled={isRemovingEnabled}
          isCroppingTrimmingEnabled={isCroppingTrimmingEnabled}
        />
      );
    case VIDEO_YOUTUBE:
      return (
        <EmbedYouTubeLinkModal
          embedUrl={embedUrl}
          onSetEmbedUrl={onSetEmbed}
          onSaveChanges={onSaveChanges}
          onRemove={onRemove}
          onClose={onClose}
          isRemovingEnabled={isRemovingEnabled}
        />
      );
    default:
      return null;
  }
};

export default UploadModal;
